/**
 * What a calendar block / list row is "about": the patient (horse), the owner
 * (client), the barn, or (last resort) the location string.
 *
 * Two render variants share one precedence rule:
 * - calendar cards get a "Patient:/Owner:/Barn:" prefix — a bare name can't
 *   tell a horse from its owner at a glance;
 * - the detail dialog prints the kind as a caption ABOVE the value, so it gets
 *   the bare name (a prefix there would read "Owner" over "Owner: Jane").
 */

export interface AppointmentSubjectSource {
  horse?: { name: string } | null;
  client?: { name: string } | null;
  barn?: { name: string } | null;
  location?: string;
}

export type AppointmentSubjectKind = 'patient' | 'owner' | 'barn' | 'location';

export const getAppointmentSubject = (
  appointment: AppointmentSubjectSource,
): { kind: AppointmentSubjectKind; name: string } =>
  appointment.horse?.name
    ? { kind: 'patient', name: appointment.horse.name }
    : appointment.client?.name
      ? { kind: 'owner', name: appointment.client.name }
      : appointment.barn?.name
        ? { kind: 'barn', name: appointment.barn.name }
        : { kind: 'location', name: appointment.location || '—' };

const PREFIX: Record<AppointmentSubjectKind, string> = {
  patient: 'Patient: ',
  owner: 'Owner: ',
  barn: 'Barn: ',
  location: '',
};

/** Calendar-card label — prefixed. */
export const getAppointmentSubjectCardLabel = (
  appointment: AppointmentSubjectSource,
): string => {
  const subject = getAppointmentSubject(appointment);
  return PREFIX[subject.kind] + subject.name;
};

/** Detail-dialog label — bare name (the caption above it names the kind). */
export const getAppointmentSubjectLabel = (
  appointment: AppointmentSubjectSource,
): string => getAppointmentSubject(appointment).name;
